import {useMemo} from 'react'
import type {Hooks, Row, TableInstance} from 'react-table'

import type {ApplyTransientFilterOpts} from '../filter-bar/search-context'
import {ensureValidPluginOrder, useCustomGlobalFilterPluginName} from './plugins'

export interface UseCustomGlobalFilterColumnProps<_D extends object> {
  disableGlobalFilter: boolean
  canFilter: boolean
}

export type UseCustomGlobalFiltersOptions<D extends object> = Partial<{
  globalFilter: (rows: Array<Row<D>>, applyTransientFilters?: ApplyTransientFilterOpts) => Array<Row<D>>
}>

export interface UseCustomGlobalFiltersInstanceProps<D extends object> {
  rows: Array<Row<D>>
  flatRows: Array<Row<D>>
  rowsById: Record<string, Row<D>>
}

/**
 * A leaner version of the default `useGlobalFilter` hook that comes with
 * `react-table`. It preserves the basic mechanism of a plug-in hook by
 * hooking the filter logic from the useSearch hook to the react-table
 * instance. It drops unnecessary properties attached to columns/rows/cells
 *
 * @param hooks react-table hooks to attach behaviour to within the plugin
 */
export const useCustomGlobalFilter = <D extends object>(hooks: Hooks<D>) => {
  hooks.useInstance.push(useInstance)
}

useCustomGlobalFilter.pluginName = useCustomGlobalFilterPluginName

function useInstance<D extends object>(instance: TableInstance<D>) {
  const {rows, flatRows, rowsById, groupedRows, globalFilter: globalFilterFn, plugins} = instance

  ensureValidPluginOrder(plugins, useCustomGlobalFilter.pluginName)

  const [globalFilteredRows, globalFilteredFlatRows, globalFilteredRowsById] = useMemo(() => {
    if (!globalFilterFn || typeof globalFilterFn === 'string') {
      return [rows, flatRows, rowsById]
    }

    const filteredFlatRows: Array<Row<D>> = []
    const filteredRowsById: {[id: Row<D>['id']]: Row<D>} = {}

    // Filters top level and nested rows
    const filterRows = (filteredRows: Array<Row<D>>) => {
      filteredRows = globalFilterFn(filteredRows)
      for (const row of filteredRows) {
        // isGrouped is `true` if the row is a group and contains subrows
        // Exclude these from flat rows and rows by id
        if (!row.isGrouped) {
          filteredFlatRows.push(row)
          filteredRowsById[row.id] = row
        }

        row.subRows = row.subRows && row.subRows.length ? filterRows(row.subRows) : row.subRows
      }
      return filteredRows
    }

    const rowsToFilter = groupedRows ? groupedRows : rows

    return [filterRows(rowsToFilter), filteredFlatRows, filteredRowsById]
  }, [flatRows, globalFilterFn, groupedRows, rows, rowsById])

  Object.assign(instance, {
    rows: globalFilteredRows,
    flatRows: globalFilteredFlatRows,
    rowsById: globalFilteredRowsById,
  })
}
